import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, Inject, PLATFORM_ID, NgZone } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { RadarService } from '../../services/radar/radar.service';

@Component({
  selector: 'ui-radar-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './radar-map.component.html',
  styleUrl: './radar-map.component.scss',
})
export class RadarMapComponent implements AfterViewInit, OnDestroy {
  // Private backing field for center
  private _center: { lat: number; lng: number } = { lat: 37.7749, lng: -122.4194 };
  
  @Input()
  set center(value: { lat: number; lng: number }) {
    this._center = value;
    const hasValidCenter =
      !!value &&
      typeof value.lat === 'number' &&
      typeof value.lng === 'number';
    // If map is not initialized yet and we now have valid center, try to initialize
    if (!this.radarMap && hasValidCenter && this.radarMapContainer) {
      this.initializeRadarMap();
    } else if(this.radarMap && hasValidCenter && typeof this.radarMap.setCenter === 'function') {
      // If map already exists and center changes, update the map center to keep it in sync
      this.ngZone.run(() => {
        this.radarMap.setCenter([value.lng, value.lat]);
      });
    }
  }
  get center(): { lat: number; lng: number } {
    return this._center;
  }
  
  @Input() zoom: number = 14;
  private _centerMarkerContent?: HTMLElement;
  @Input()
  set centerMarkerContent(value: HTMLElement | undefined) {
    this._centerMarkerContent = value;
    this.tryUpdateMarkers();
  }
  get centerMarkerContent(): HTMLElement | undefined {
    return this._centerMarkerContent;
  }
  @Input() radarStyle?: string;  // Optional - if not provided, uses style from RadarService config
  @Input() pageType: string = 'parking';
  @Input() showInfoBoxOnMobile: boolean = false;  // Default to false for backward compatibility

  /** Show/hide map controls (e.g. fullscreen). Default true. */
  @Input() showMapControls: boolean = true;
  /** Show/hide fullscreen button. Default true. */
  @Input() showFullscreenControl: boolean = true;
  /** Fullscreen icon URL. Defaults to assets/images/map-fullscreen.svg (app must provide asset). */
  @Input() fullscreenIconUrl: string = 'assets/images/map-fullscreen.svg';
  /** Exit fullscreen icon URL. Defaults to assets/images/map-not-fullscreen.svg (app must provide asset). */
  @Input() exitFullscreenIconUrl: string = 'assets/images/map-not-fullscreen.svg';

  // Private backing fields for properties with timing issues
  private _markers: any[] = [];
  private _nearbyMarkerElements: { [id: number]: HTMLElement } = {};
  private _activeMarkerId: any;
  
  // Getter/Setter for markers to handle timing issue
  @Input()
  set markers(value: any[]) {
    this._markers = value;
    this.tryUpdateMarkers();
  }
  get markers(): any[] {
    return this._markers;
  }

  // Getter/Setter for nearbyMarkerElements to handle timing issue
  @Input()
  set nearbyMarkerElements(value: { [id: number | string]: HTMLElement }) {
    this._nearbyMarkerElements = value;
    this.tryUpdateMarkers();
  }
  get nearbyMarkerElements(): { [id: number | string]: HTMLElement } {
    return this._nearbyMarkerElements;
  }

  // Getter/Setter for activeMarkerId to handle active marker updates (styling/highlight)
  @Input()
  set activeMarkerId(value: any) {
    this._activeMarkerId = value;
    this.updateActiveMarker();
  }
  get activeMarkerId(): any {
    return this._activeMarkerId;
  }

  /**
   * Optional. When set, only this marker's infobox is shown (e.g. from map click).
   * When not set, infobox follows activeMarkerId (legacy behavior).
   * Use this to avoid opening infobox on list hover when activeMarkerId is used for highlight only.
   */
  @Input()
  set showInfoBoxForMarkerId(value: any) {
    this._showInfoBoxForMarkerId = value;
    this.updateActiveMarker();
  }
  get showInfoBoxForMarkerId(): any {
    return this._showInfoBoxForMarkerId;
  }
  private _showInfoBoxForMarkerId: any;

  @Output() mapReady = new EventEmitter<any>();
  @Output() markerClick = new EventEmitter<any>();
  @Output() infoboxClose = new EventEmitter<void>();
  @Output() infoBoxClick = new EventEmitter<any>();
  @Output() autoRepairBookNowBtnClick = new EventEmitter<number>();
  
  @ViewChild('radarMapContainer', { static: false }) radarMapContainer?: ElementRef;
  @ViewChild('mapWrapper', { static: false }) mapWrapper?: ElementRef;

  radarMap: any;
  radarMarkers: any[] = [];
  isBrowser: boolean;
  isMapReady: boolean = false;
  /** Whether the map is in fullscreen */
  isFullscreen: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    private radarService: RadarService,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Toggle fullscreen on the map wrapper */
  toggleFullscreen(): void {
    if (!this.isBrowser || !this.mapWrapper?.nativeElement) return;
    const el = this.mapWrapper.nativeElement as HTMLElement;
    if (!this.isFullscreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen().then(() => {
          this.ngZone.run(() => (this.isFullscreen = true));
        }).catch(() => {});
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen().then(() => {
          this.ngZone.run(() => (this.isFullscreen = false));
        }).catch(() => {});
      }
    }
  }

  private fullscreenChangeListener = () => {
    this.ngZone.run(() => {
      this.isFullscreen = !!this.document.fullscreenElement;
    });
  };

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.document.addEventListener('fullscreenchange', this.fullscreenChangeListener);
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          if (!this.radarMap) {
            this.initializeRadarMap();
          }
        }, 100);
      });
    }
  }

  private initializeRadarMap() {
    if (!this.radarMapContainer) {
      console.error('Radar map container not found');
      return;
    }

    if (!this.center || typeof this.center.lat !== 'number' || typeof this.center.lng !== 'number') {
      console.warn('Center coordinates not ready yet, skipping initialization');
      return;
    }

    const Radar = this.radarService.getRadar();
    
    if (!Radar) {
      console.error('Radar SDK not loaded');
      return;
    }
    if (!Radar.ui || typeof Radar.ui.map !== 'function') {
      console.error('Radar SDK map UI not available. Ensure Radar.initialize(publishableKey) was called and the SDK has loaded.');
      return;
    }

    try {
      // Use component's radarStyle input if provided, otherwise fall back to RadarService config, then to default
      const mapStyle = this.radarStyle || this.radarService.getMapStyle() || 'radar-default-v1';
      
      // Create Radar map
      this.radarMap = Radar.ui.map({
        container: this.radarMapContainer.nativeElement,
        style: mapStyle,
        center: [this.center.lng, this.center.lat],
        zoom: this.zoom || 14
      });

      // `load` fires when the style JSON is parsed; sprites/images may still be loading.
      // MapLibre can throw `this.style.imageManager` null if markers/DOM attach before the first full render.
      // Wait for `idle` (first frame with style/renderer fully ready), same pattern as Google map idle gate.
      this.radarMap.on('load', () => {
        const finishWhenStable = () => {
          if (this.isMapReady) {
            return;
          }
          this.isMapReady = true;
          this.ngZone.run(() => {
            this.mapReady.emit(this.radarMap);
            this.tryUpdateMarkers();
          });
        };

        const mapAny = this.radarMap as {
          once?: (ev: string, fn: () => void) => void;
          isStyleLoaded?: () => boolean;
        };
        if (typeof mapAny.once === 'function') {
          mapAny.once('idle', finishWhenStable);
        } else {
          this.radarMap.on?.('idle', finishWhenStable);
        }

        setTimeout(() => {
          if (!this.isMapReady) {
            finishWhenStable();
          }
        }, 5000);
      });

    } catch (error) {
      console.error('Error initializing Radar map:', error);
    }
  }

  /**
   * Attempts to update markers only when ALL conditions are met:
   * - Map is ready
   * - Markers data exists
   * - Marker DOM elements exist
   * This prevents race conditions between async operations
   */
  private tryUpdateMarkers() {
    const hasMarkers = this._markers && this._markers.length > 0;
    const hasMarkerElements = Object.keys(this._nearbyMarkerElements).length > 0;
    if (this.isMapReady && hasMarkers && hasMarkerElements) {
      this.updateMarkers();
    }
  }

  updateMarkers() {
    if (!this.radarMap || !this.isMapReady) {
      return;
    }

    // Guard: MapLibre style must exist before marker layers use sprite/image pipeline (imageManager).
    const mapAny = this.radarMap as { style?: unknown; isStyleLoaded?: () => boolean };
    if (mapAny.style == null) {
      const m = this.radarMap as { once?: (ev: string, fn: () => void) => void };
      if (typeof m.once === 'function') {
        m.once('idle', () => this.ngZone.run(() => this.updateMarkers()));
      } else {
        setTimeout(() => this.ngZone.run(() => this.updateMarkers()), 100);
      }
      return;
    }
    if (typeof mapAny.isStyleLoaded === 'function' && !mapAny.isStyleLoaded()) {
      const m = this.radarMap as { once?: (ev: string, fn: () => void) => void };
      if (typeof m.once === 'function') {
        m.once('idle', () => this.ngZone.run(() => this.updateMarkers()));
      } else {
        setTimeout(() => this.ngZone.run(() => this.updateMarkers()), 100);
      }
      return;
    }

    const Radar = this.radarService.getRadar();
    
    // Clear existing markers
    this.clearMarkers();

    // Add center marker
    if (this._centerMarkerContent) {
      const centerMarkerClone = this._centerMarkerContent.cloneNode(true) as HTMLElement;

      const centerMarker = (Radar.ui.marker({
        element: centerMarkerClone,
        anchor: 'center'
      } as Record<string, unknown>) as { setLngLat: (lngLat: [number, number]) => { addTo: (map: unknown) => unknown }; addTo: (map: unknown) => unknown })
        .setLngLat([this.center.lng, this.center.lat])
        .addTo(this.radarMap);
      
      this.radarMarkers.push(centerMarker);
    }

    // Add nearby markers
    this.markers.forEach((marker) => {
      // Radar expects [lng, lat]
      const lng = marker.position[1];
      const lat = marker.position[0];
      const position: [number, number] = [lng, lat];

      // Use the marker content from nearbyMarkerElements (provided by parent)
      const markerElement = this.nearbyMarkerElements[marker.id];
      
      if (!markerElement) {
        return;
      }

      // Clone the element to avoid DOM manipulation issues
      const markerElementClone = markerElement.cloneNode(true) as HTMLElement;

      const radarMarker = (Radar.ui.marker({
        element: markerElementClone,
        anchor: 'center'
      } as Record<string, unknown>) as { setLngLat: (lngLat: [number, number]) => { addTo: (map: unknown) => unknown }; addTo: (map: unknown) => unknown })
        .setLngLat(position)
        .addTo(this.radarMap);

      // Add click event to the cloned element
      markerElementClone.addEventListener('click', () => {
        this.ngZone.run(() => {
          this.markerClick.emit(marker);
        });
      });

      if(this.pageType == 'parking') {
        const infoBox = markerElementClone.querySelector('.infobx');
        if(infoBox) {
          infoBox.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
        const closeBtn = markerElementClone.querySelector('.infobx button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              if (infoBox) {
                infoBox.classList.remove('showinfo');
              }
              this.infoboxClose.emit();
            });
          });
        }
      } else if(this.pageType == 'gas') {
        const infoBox = markerElementClone.querySelector('.infobx');
        if(infoBox) {
          infoBox.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
        const closeBtn = markerElementClone.querySelector('.infobx button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              if (infoBox) {
                infoBox.classList.remove('showinfo');
              }
              this.infoboxClose.emit();
            });
          });
        }
      } else if(this.pageType == 'autorepair') {
        const infoBoxBookNowBtn = markerElementClone.querySelector('.infobx button.book-now-btn');
        if(infoBoxBookNowBtn) {
          infoBoxBookNowBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.autoRepairBookNowBtnClick.emit(marker.listingId);
            });
          });
        }
      } else if(this.pageType == 'city-parking') {
        // Add click event to close button in infobox
        const closeBtn = markerElementClone.querySelector('.infobx .popimg button[aria-label="close"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoboxClose.emit();
            });
          });
        }
        // Add click event to infobox for navigation to details page
        const infoBoxBtn = markerElementClone.querySelector('.infobx .popcont .rtesr .book-now');
        if(infoBoxBtn) {
          infoBoxBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.ngZone.run(() => {
              this.infoBoxClick.emit(marker);
            });
          });
        }
      }
      
      this.radarMarkers.push(radarMarker);
    });
  }

  private clearMarkers() {
    if (this.radarMarkers.length > 0) {
      this.radarMarkers.forEach(marker => {
        try {
          marker.remove();
        } catch (e) {
          console.error('Error removing marker:', e);
        }
      });
      this.radarMarkers = [];
    }
  }

  /**
   * Updates the active marker styling and infobox visibility based on activeMarkerId
   */
  private updateActiveMarker() {
    if (!this.radarMapContainer) return;
    
    // Get CSS selectors based on page type
    const config = this.getMarkerConfig(this.pageType);
    
    // Remove active classes from all markers and hide all infoboxes
    const allMarkers = this.radarMapContainer.nativeElement.querySelectorAll(config.containerSelector);
    allMarkers.forEach((marker: Element) => {
      marker.classList.remove(config.containerActiveClass);
      const markerElement = marker.querySelector(config.markerSelector);
      if (markerElement) {
        markerElement.classList.remove(config.markerActiveClass);
      }
      
      // Hide infobox
      const infobox = marker.querySelector('.infobx');
      if (infobox) {
        infobox.classList.remove('showinfo');
      }
    });
    
    // Add active class to current marker (for highlight on hover/click)
    if (this.activeMarkerId !== null && this.activeMarkerId !== undefined) {
      const activeMarker = this.radarMapContainer.nativeElement
        .querySelector(`${config.markerIdPrefix}${this.activeMarkerId}`)
        ?.closest(config.containerSelector);
      
      if (activeMarker) {
        activeMarker.classList.add(config.containerActiveClass);
        const markerElement = activeMarker.querySelector(config.markerSelector);
        if (markerElement) {
          markerElement.classList.add(config.markerActiveClass);
        }
      }
    }

    // Show infobox only for showInfoBoxForMarkerId when provided (e.g. map click), else for activeMarkerId (legacy)
    const infoboxMarkerId = this.showInfoBoxForMarkerId !== undefined && this.showInfoBoxForMarkerId !== null && this.showInfoBoxForMarkerId !== ''
      ? this.showInfoBoxForMarkerId
      : this.activeMarkerId;
    const hasValidInfoboxId = infoboxMarkerId !== null && infoboxMarkerId !== undefined && infoboxMarkerId !== '';
    if (hasValidInfoboxId && this.isBrowser && (window.innerWidth > 767 || this.showInfoBoxOnMobile)) {
      const infoboxMarker = this.radarMapContainer.nativeElement
        .querySelector(`${config.markerIdPrefix}${infoboxMarkerId}`)
        ?.closest(config.containerSelector);
      if (infoboxMarker) {
        const infobox = infoboxMarker.querySelector('.infobx');
        if (infobox) {
          infobox.classList.add('showinfo');
        }
      }
    }
  }

  /**
   * Get marker configuration based on page type
   */
  private getMarkerConfig(pageType: string) {
    // Default config used by all page types unless overridden
    const defaultConfig = {
      containerSelector: '.marker-cntr',
      markerSelector: '.nw-mrkr',
      containerActiveClass: 'active',
      markerActiveClass: 'activeMarker',
      markerIdPrefix: '#mrkerd_'
    };
    
    // Only define page-specific configs when they differ from default
    const pageSpecificConfigs: { [key: string]: Partial<typeof defaultConfig> } = {
      'city-parking': {
        containerSelector: '.cst-marker',
      },
      'gas': {
        containerSelector: '.gas-marker-cntr',
        markerSelector: '.gas-marker',
      }
    };
    
    // Merge: Start with default, then override with page-specific properties
    return {
      ...defaultConfig,
      ...(pageSpecificConfigs[pageType] || {})
    };
  }

  ngOnDestroy() {
    if (this.isBrowser && this.document) {
      this.document.removeEventListener('fullscreenchange', this.fullscreenChangeListener);
    }
    if (this.radarMap) {
      try {
        this.clearMarkers();
        this.radarMap.remove();
      } catch (e) {
        console.error('Error destroying Radar map:', e);
      }
    }
  }
}
